/* A blocked patient switch preserves all open drafts and requires the current
 * microphone session to finish. The dialog cannot override audio ownership. */
import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useEffect, useRef } from "react";
import { acquireBodyScrollLock } from "../lib/bodyScrollLock";

function Glyph({
  size,
  width = 1.7,
  children,
}: {
  readonly size: number;
  readonly width?: number;
  readonly children: ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}
function IconAlert({ size = 16 }: { size?: number }) {
  return (
    <Glyph size={size} width={1.8}>
      <path d="M12 3l10 17H2z" />
      <path d="M12 10v5M12 18h.01" />
    </Glyph>
  );
}

export function EncounterSwitchConfirm({
  currentName,
  busy,
  onKeepCurrent,
  onOpenNew,
}: {
  /** Display name of the patient who owns the microphone (if known). */
  readonly currentName: string | null;
  readonly busy: boolean;
  readonly onKeepCurrent: () => void;
  readonly onOpenNew: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const keepButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const releaseScrollLock = acquireBodyScrollLock();
    const focusTimer = window.setTimeout(() => keepButtonRef.current?.focus(), 0);
    // Global accelerators (Ctrl/Cmd+K command palette) open UNDER this scrim
    // and would steal focus out of the alertdialog — block them at capture
    // phase while it is up.
    const blockAccelerators = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("keydown", blockAccelerators, true);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", blockAccelerators, true);
      releaseScrollLock();
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, []);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onKeepCurrent();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>("button:not([disabled])") ?? [],
    );
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    // Clicking the scrim takes the safe branch — same as Escape. (A plain
    // onClick on the scrim div would also catch bubbled dialog clicks, so the
    // target check keeps it to the scrim itself.)
    // biome-ignore lint/a11y/noStaticElementInteractions: scrim dismissal backs up the keyboard paths (Escape + the focused safe button); it is not the primary control.
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users have Escape and the focus-seeded buttons.
    <div
      className="ib-confirm-scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onKeepCurrent();
      }}
    >
      <section
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ib-confirm-title"
        aria-describedby="ib-confirm-detail"
        className="ib-confirm"
        onKeyDown={handleKeyDown}
      >
        <h2 id="ib-confirm-title" className="ib-confirm-title">
          <IconAlert />
          Finish the microphone session first
        </h2>
        <p id="ib-confirm-detail" className="ib-confirm-detail">
          {currentName
            ? `Audio belongs to ${currentName}. `
            : "Audio stays with its original patient. "}
          Stop recording and wait for transcription to finish before switching or closing this
          encounter. Other encounter drafts stay open.
        </p>
        <div className="ib-confirm-actions">
          <button
            ref={keepButtonRef}
            type="button"
            className="ib-confirm-btn ghost"
            onClick={onKeepCurrent}
          >
            Return to recording
          </button>
          <button
            type="button"
            className="ib-confirm-btn primary"
            disabled={busy}
            onClick={onOpenNew}
          >
            Continue
          </button>
        </div>
      </section>
    </div>
  );
}
